import { apiSlice } from './apiSlice';

export interface PosRoomSettings {
  orderRetentionDays?: number;
  cardCaps?: {
    perTxnMinor?: number | string;
    dailyMinor?: number | string;
    managerApprovalAboveMinor?: number | string;
  };
  reviewTolerance?: number;
}

export interface PosRoom {
  id: string;
  orgId?: string;
  storeId: string;
  name: string;
  status: 'active' | 'disabled' | string;
  settings?: PosRoomSettings | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PosRoomCreateResult extends PosRoom {
  /** Returned only when the room is created. Treat as a credential. */
  hmacKey: string;
}

export interface PosRoomNode {
  id: string;
  roomId: string;
  nodeId: string;
  label?: string | null;
  role?: string | null;
  status?: string | null;
  currentEpoch?: number | null;
  lastSeq?: string | null;
  lastHeartbeatAt?: string | null;
  isStale?: boolean;
}

export type PosRoomReviewKind =
  | 'pricing_mismatch'
  | 'conflict_unresolved'
  | 'negative_stock'
  | 'offline_auth_declined'
  | 'receipt_range_overlap'
  | string;

export interface PosRoomReviewItem {
  id: string;
  roomId?: string | null;
  kind: PosRoomReviewKind;
  severity: 'low' | 'medium' | 'high' | 'critical' | string;
  status: 'open' | 'resolved' | 'dismissed' | string;
  payload?: Record<string, unknown>;
  createdAt?: string;
}

export interface PosRoomCommand {
  id: string;
  type: string;
  status: string;
  targetNodeId?: string | null;
  createdAt?: string;
  expiresAt?: string;
}

export const posRoomsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPosRooms: builder.query<PosRoom[], void>({
      // GET /v1/pos-rooms; pos-rooms:room:read on resource pos-rooms.
      query: () => '/pos-rooms/v1/pos-rooms',
      providesTags: (rooms) => rooms
        ? [...rooms.map(({ id }) => ({ type: 'StoreEdge' as const, id })), { type: 'StoreEdge', id: 'ROOMS' }]
        : [{ type: 'StoreEdge', id: 'ROOMS' }],
    }),
    getPosRoom: builder.query<PosRoom, string>({
      // GET /v1/pos-rooms/:id; pos-rooms:room:read on resource pos-rooms.
      query: (roomId) => `/pos-rooms/v1/pos-rooms/${encodeURIComponent(roomId)}`,
      providesTags: (_result, _error, id) => [{ type: 'StoreEdge', id }],
    }),
    createPosRoom: builder.mutation<PosRoomCreateResult, { storeId: string; name: string; settings?: PosRoomSettings }>({
      // POST /v1/pos-rooms; pos-rooms:room:create on resource pos-rooms.
      query: (body) => ({ url: '/pos-rooms/v1/pos-rooms', method: 'POST', body }),
      invalidatesTags: [{ type: 'StoreEdge', id: 'ROOMS' }],
    }),
    updatePosRoom: builder.mutation<PosRoom, { roomId: string; body: Partial<Pick<PosRoom, 'name' | 'status' | 'settings'>> }>({
      // PATCH /v1/pos-rooms/:id; pos-rooms:room:update on resource pos-rooms.
      query: ({ roomId, body }) => ({
        url: `/pos-rooms/v1/pos-rooms/${encodeURIComponent(roomId)}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { roomId }) => [
        { type: 'StoreEdge', id: roomId },
        { type: 'StoreEdge', id: 'ROOMS' },
      ],
    }),
    getPosRoomNodes: builder.query<PosRoomNode[], string>({
      // GET /v1/pos-rooms/:roomId/nodes; pos-rooms:node:read on resource pos-rooms.
      query: (roomId) => `/pos-rooms/v1/pos-rooms/${encodeURIComponent(roomId)}/nodes`,
      providesTags: (_result, _error, roomId) => [{ type: 'StoreEdge', id: `NODES-${roomId}` }],
    }),
    getPosRoomReviews: builder.query<PosRoomReviewItem[], { roomId: string; status?: 'open' | 'resolved' | 'dismissed'; limit?: number }>({
      // GET /v1/pos-rooms/:roomId/review-items; pos-rooms:review:read on resource pos-rooms.
      query: ({ roomId, ...params }) => ({
        url: `/pos-rooms/v1/pos-rooms/${encodeURIComponent(roomId)}/review-items`,
        params: { ...(params.status ? { status: params.status } : {}), ...(params.limit ? { limit: params.limit } : {}) },
      }),
      providesTags: (items, _error, { roomId }) => items
        ? [...items.map(({ id }) => ({ type: 'StoreEdge' as const, id: `REVIEW-${id}` })), { type: 'StoreEdge', id: `REVIEWS-${roomId}` }]
        : [{ type: 'StoreEdge', id: `REVIEWS-${roomId}` }],
    }),
    issuePosRoomCommand: builder.mutation<PosRoomCommand, {
      roomId: string;
      body: { type: 'disable' | 'enable'; targetNodeId: string; expiresInSeconds: number };
    }>({
      // POST /v1/pos-rooms/:roomId/commands; pos-rooms:command:issue on resource pos-rooms.
      query: ({ roomId, body }) => ({
        url: `/pos-rooms/v1/pos-rooms/${encodeURIComponent(roomId)}/commands`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { roomId }) => [
        { type: 'StoreEdge', id: `NODES-${roomId}` },
        { type: 'StoreEdge', id: `COMMANDS-${roomId}` },
      ],
    }),
    resolvePosRoomReview: builder.mutation<PosRoomReviewItem, { roomId: string; itemId: string }>({
      // POST /v1/pos-rooms/:roomId/review-items/:itemId/resolve; pos-rooms:review:update.
      query: ({ roomId, itemId }) => ({
        url: `/pos-rooms/v1/pos-rooms/${encodeURIComponent(roomId)}/review-items/${encodeURIComponent(itemId)}/resolve`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { roomId, itemId }) => [
        { type: 'StoreEdge', id: `REVIEW-${itemId}` },
        { type: 'StoreEdge', id: `REVIEWS-${roomId}` },
      ],
    }),
    dismissPosRoomReview: builder.mutation<PosRoomReviewItem, { roomId: string; itemId: string }>({
      // POST /v1/pos-rooms/:roomId/review-items/:itemId/dismiss; pos-rooms:review:update.
      query: ({ roomId, itemId }) => ({
        url: `/pos-rooms/v1/pos-rooms/${encodeURIComponent(roomId)}/review-items/${encodeURIComponent(itemId)}/dismiss`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { roomId, itemId }) => [
        { type: 'StoreEdge', id: `REVIEW-${itemId}` },
        { type: 'StoreEdge', id: `REVIEWS-${roomId}` },
      ],
    }),
  }),
});

export const {
  useGetPosRoomsQuery,
  useGetPosRoomQuery,
  useCreatePosRoomMutation,
  useUpdatePosRoomMutation,
  useGetPosRoomNodesQuery,
  useGetPosRoomReviewsQuery,
  useIssuePosRoomCommandMutation,
  useResolvePosRoomReviewMutation,
  useDismissPosRoomReviewMutation,
} = posRoomsApi;
